import { useQueryClient } from "@tanstack/react-query";
import { logger, useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";

/**
 * Query key prefix of the plan detail's activation sweep (`$task-plan/index.tsx`).
 * Exported so the page and this hook agree.
 */
export const ACTIVATION_SWEEP_KEY = "activation-task-activations";

// Deliberate duplicate of `outbound/.../tasks/-data/use-plans.ts`'s `isConflict`:
// the internal and outbound route trees are kept uncoupled and must not import
// from each other.
/** A 409 from the service. `openapi-react-query` rejects with the `ErrorResponse` body, not the status. */
export function isConflict(error: unknown): boolean {
  // The mock's code string (`mocks/store/mutations.ts`); change here if the real service differs.
  return (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    error.error === "conflict"
  );
}

/**
 * `POST /{orgId}/task-assignment`, `kind: "remove_assignment"`: return
 * assigned-but-unclaimed tasks to the pool.
 *
 * The service answers 204 and dispatches asynchronously, so the refetch in
 * `finally` can still read the pre-dispatch rows (as `useAssignTasks`). The
 * hook does not toast; the caller does.
 */
export function useUnassignTasks(): {
  unassign: (taskIds: ReadonlyArray<string>) => Promise<void>;
  isPending: boolean;
} {
  const { orgId, warehouseId } = useAssertedTenancyContext();
  const client = useTaskPlannerApi();
  const queryClient = useQueryClient();
  const mutation = client.useMutation("post", "/{orgId}/task-assignment");

  const unassign = async (taskIds: ReadonlyArray<string>) => {
    try {
      await mutation.mutateAsync({
        params: {
          path: { orgId },
          query: { warehouseExtId: warehouseId },
        },
        body: { kind: "remove_assignment", task_ids: [...taskIds] },
      });
      logger.info("Manual task unassignment accepted", {
        count: taskIds.length,
      });
    } finally {
      await queryClient.invalidateQueries({ queryKey: [ACTIVATION_SWEEP_KEY] });
    }
  };

  return { unassign, isPending: mutation.isPending };
}
